// Groupes d'un exercice (D-052), comme dans Playbook : autant de groupes que le coach
// veut, chacun avec un nom et une couleur de chasuble, composés avec les présentes.
// Rangés dans l'exercice du plan (PlanItem.groups). Logique pure, hasard injecté.

export type Bib = 'red' | 'blue' | 'yellow' | 'green' | 'orange' | 'purple';
export const BIBS: Bib[] = ['red', 'blue', 'yellow', 'green', 'orange', 'purple'];
/** Mêmes couleurs que les chasubles de l'éditeur de schémas (D-051). */
export const BIB_HEX: Record<Bib, string> = {
  red: '#EF4444', blue: '#3B82F6', yellow: '#FACC15', green: '#22C55E', orange: '#F97316', purple: '#A855F7',
};
export const BIB_INK: Record<Bib, string> = {
  red: '#FFFFFF', blue: '#FFFFFF', yellow: '#111827', green: '#FFFFFF', orange: '#FFFFFF', purple: '#FFFFFF',
};
export const BIB_LABEL: Record<Bib, string> = {
  red: 'rouge', blue: 'bleue', yellow: 'jaune', green: 'verte', orange: 'orange', purple: 'violette',
};
export const isBib = (v: unknown): v is Bib => typeof v === 'string' && (BIBS as string[]).includes(v);

export interface ExoGroup { name: string; bib: Bib | null; players: string[] }

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/** Un groupe de plus : « Groupe C », avec la première chasuble pas encore prise. */
export function addGroup(groups: ExoGroup[]): ExoGroup[] {
  const names = new Set(groups.map(g => g.name));
  let k = groups.length;
  while (names.has(`Groupe ${LETTERS[k % 26]}${k >= 26 ? Math.floor(k / 26) + 1 : ''}`)) k++;
  const used = new Set(groups.map(g => g.bib));
  return [...groups, { name: `Groupe ${LETTERS[k % 26]}${k >= 26 ? Math.floor(k / 26) + 1 : ''}`, bib: BIBS.find(b => !used.has(b)) ?? null, players: [] }];
}

/** Place une joueuse dans le groupe `to` (et la retire des autres) ; `to` < 0 = sans groupe. */
export function assign(groups: ExoGroup[], person: string, to: number): ExoGroup[] {
  return groups.map((g, i) => {
    const rest = g.players.filter(p => p !== person);
    return { ...g, players: i === to ? [...rest, person] : rest };
  });
}

/** Les présentes qui n'ont pas de groupe. */
export const unassigned = (groups: ExoGroup[], present: string[]) => {
  const inGroup = new Set(groups.flatMap(g => g.players));
  return present.filter(p => !inGroup.has(p));
};

/** Mélange : toutes les présentes, au hasard, en groupes équilibrés (écart d'une au plus). */
export function shuffle(groups: ExoGroup[], present: string[], rand: () => number = Math.random): ExoGroup[] {
  if (!groups.length) return groups;
  const pool = [...present];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [pool[i], pool[j]] = [pool[j]!, pool[i]!];
  }
  return groups.map((g, i) => ({ ...g, players: pool.filter((_, k) => k % groups.length === i) }));
}

/** Les groupes vus le jour J : sans les absentes (elles restent rangées si elles reviennent). */
export const presentOnly = (groups: ExoGroup[], present: string[]) => {
  const here = new Set(present);
  return groups.map(g => ({ ...g, players: g.players.filter(p => here.has(p)) }));
};

/** Nettoie ce qui vient du document (JSON) : rien d'inattendu ne passe. */
export function readGroups(v: unknown): ExoGroup[] {
  if (!Array.isArray(v)) return [];
  return v.filter(g => g && typeof g === 'object').slice(0, 26).map(g => {
    const o = g as Record<string, unknown>;
    return {
      name: typeof o.name === 'string' && o.name.trim() ? o.name.trim().slice(0, 40) : 'Groupe',
      bib: isBib(o.bib) ? o.bib : null,
      players: Array.isArray(o.players) ? [...new Set(o.players.filter((p): p is string => typeof p === 'string'))] : [],
    };
  });
}
